import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage immediately (synchronous)
  const [user, setUser] = useState(() => {
    try {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  });
  
  const [userType, setUserType] = useState(() => {
    return localStorage.getItem('userType') || null;
  });
  
  const [loading, setLoading] = useState(false);

  // Restore session from localStorage on mount and on focus
  const restoreSession = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      const type = localStorage.getItem('userType');
      const userData = localStorage.getItem('userData');
      
      if (token && type && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setUserType(type);
        return true;
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      // Clear corrupted data
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('userData');
    }
    return false;
  }, []);

  useEffect(() => {
    restoreSession();
    setLoading(false);
    // Note: Removed focus/visibility listeners to prevent auto-refresh on tab switch
    // Session is restored only on initial mount - user can manually refresh if needed
  }, [restoreSession]);

  const login = (token, type, userData) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('userType', type);
      localStorage.setItem('userData', JSON.stringify(userData));
      setUser(userData);
      setUserType(type);
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userData');
    setUser(null);
    setUserType(null);
  };

  const getToken = () => localStorage.getItem('token');
  
  // Check if session is valid
  const isAuthenticated = () => {
    return !!(localStorage.getItem('token') && localStorage.getItem('userData'));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      userType, 
      login, 
      logout, 
      loading, 
      getToken,
      isAuthenticated,
      restoreSession 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
